import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  BriefcaseIcon,
  ClockIcon,
  DollarSignIcon,
  MapPinIcon,
  PencilIcon,
  ShieldCheckIcon,
  type LucideIcon,
} from "lucide-react";

import { AddLogisticsToEvaluationControl } from "./AddLogisticsToEvaluationControl";
import { CandidacySourceCitations } from "./CandidacySourceCitations";
import { DrugAndBackgroundEditor } from "./DrugAndBackgroundEditor";
import { LocationEditor } from "./LocationEditor";
import { PayEditor } from "./PayEditor";
import {
  toAiCallInsightCitationSource,
  toCitationSources,
  type PaperTrailCitationSource,
} from "./paperTrailCitations";
import { ShiftPreferenceEditor } from "./ShiftPreferenceEditor";

import { Column, Row, Text } from "@/components";
import { Button } from "@ui/button";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetJob } from "@/hooks/queries/useGetJob";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetPaperTrailCitations } from "@/hooks/queries/useGetPaperTrailCitations";
import { useGetUsers } from "@/hooks/queries/useGetUsers";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidacyEvaluations } from "@/lib/candidacyEvaluations";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { cn } from "@/lib/utils";

const EMPTY_STATE_MESSAGE = "-";
export const CandidacyLogisticsSection = ({
  onOpenSheetTab,
}: {
  onOpenSheetTab?: (tab: "notes" | "records") => void;
}) => {
  /* Context */
  const { candidacyId, isReadOnly, jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const candidacyInfo = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  }).data;
  const profile = useGetCandidacySheetData({ jobId, userId: workerId }).data;
  // Job shifts and comp range feed the needs-follow-up mechanisms.
  // The job page underneath has already primed this cache.
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const job = useGetJob({
    companyId: currentCompanyId,
    jobId: currentJobId,
  }).data;
  const citations = useGetPaperTrailCitations({
    userId: workerId,
  }).data;
  const payCitationRows = (citations?.fieldCitations ?? []).filter(
    (row) =>
      row.entityField === "currentPay" || row.entityField === "desiredPay",
  );
  const drugAndBackgroundCitationRows = (
    citations?.fieldCitations ?? []
  ).filter(
    (row) =>
      row.entityField === "drugScreenStatus" ||
      row.entityField === "hasCriminalRecord",
  );
  const shiftCitationRows = citations?.shiftCitations ?? [];
  // Quote-backed evidence from the latest Stevie call summary, for the rows the
  // paper trail doesn't cover. Rendered like citations, but display-only.
  const aiCallInsights = candidacyInfo?.aiCallInsights;
  const commuteCallNotes = aiCallInsights?.locationAndCommute?.notes;
  // Edits cite a staffer id. While the data lives in guac, it is cached app-
  // wide, so this is a cache read in practice rather than a request per sheet.
  const users = useGetUsers().data;
  const stafferNamesById = new Map(
    (users ?? []).map((user) => [
      user.id,
      `${user.firstName} ${user.lastName}`.trim(),
    ]),
  );
  // The signed-in staffer authors any edit made here.
  const currentUser = useGetCurrentUser();

  /* React Hooks */
  // One editor open at a time across the whole section, keyed by row label.
  // Starting an edit on another row closes this one.
  const [editingLabel, setEditingLabel] = useState<string>();
  // Clicking outside the open editor cancels the edit. Nothing saves until
  // Done / a selection, so dismissing is always safe.
  const editorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editingLabel) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element;
      if (editorRef.current?.contains(target)) return;
      // Radix portals its Select dropdowns to document.body, so an option
      // click is outside the editor's subtree but still part of the edit.
      if (target.closest("[data-radix-popper-content-wrapper]")) return;
      setEditingLabel(undefined);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [editingLabel]);

  /* Helpers */
  const commuteTimeMins = candidacyInfo?.commuteTimeMins;
  const currentPay = profile?.profile.currentPay;
  const desiredPay = profile?.profile.desiredPay;
  // Pay renders in the unit the candidate stated it in, not the job's.
  const payParts = [
    currentPay != null ? `Current ${formatting.payWithUnit(currentPay)}` : null,
    desiredPay != null ? `Desired ${formatting.payWithUnit(desiredPay)}` : null,
  ].filter(Boolean);
  const commuteParts = [
    profile?.profile.location
      ? locations.stripCountrySuffix(profile.profile.location)
      : null,
    candidacyInfo?.commuteDistanceMiles != null
      ? `${candidacyInfo.commuteDistanceMiles.toString()} mi`
      : null,
    commuteTimeMins != null
      ? candidacyEvaluations.formatCommuteTime(commuteTimeMins)
      : null,
  ].filter(Boolean);

  // Needs-follow-up flags derive from live data, so a staffer fixing a field
  // through the inline editors clears the chip on its own. Undefined (rather
  // than flagged) while the backing query is still loading — an unknown we
  // haven't fetched yet isn't an unknown worth chasing.
  const payNeedsFollowUp = profile
    ? candidacyEvaluations.payNeedsFollowUp({
        compensationMax: job?.compensationMax,
        compensationType: job?.compensationType,
        currentPay,
        desiredPay,
      })
    : undefined;
  const commuteNeedsFollowUp = candidacyInfo
    ? candidacyEvaluations.commuteNeedsFollowUp({ commuteTimeMins })
    : undefined;
  const shiftNeedsFollowUp =
    profile && job
      ? candidacyEvaluations.shiftNeedsFollowUp({
          jobShifts: job.shiftType,
          shiftPreferences: profile.shiftPreferences,
        })
      : undefined;
  const drugAndBackgroundNeedsFollowUp = profile
    ? candidacyEvaluations.drugAndBackgroundNeedsFollowUp({
        drugScreenStatus: profile.profile.drugScreenStatus,
        hasCriminalRecord: profile.profile.hasCriminalRecord,
      })
    : undefined;

  const SHIFT_DISPLAY = [
    { label: "1st", shift: "FIRSTSHIFT" },
    { label: "2nd", shift: "SECONDSHIFT" },
    { label: "3rd", shift: "THIRDSHIFT" },
    { label: "Weekends", shift: "WEEKEND" },
  ] as const;
  // Stance-major order: what they want leads, what they are open to follows,
  // refusals trail. Shift order breaks ties within each tier.
  const SHIFT_STANCE_ORDER = ["PREFERRED", "YES", "NO"] as const;
  // Same wording the paper-trail citations use, so a stance reads identically
  // wherever it appears.
  const SHIFT_STANCE_LABELS = {
    NO: "No",
    PREFERRED: "Preferred",
    YES: "Yes",
  } as const;
  const formatShiftStances = () => {
    const stances = profile?.shiftPreferences ?? [];
    const parts = SHIFT_STANCE_ORDER.flatMap((status) =>
      SHIFT_DISPLAY.flatMap(({ label, shift }) => {
        const stance = stances.find((entry) => entry.shift === shift);
        if (stance?.status !== status) return [];
        return [`${label} ${SHIFT_STANCE_LABELS[status]}`];
      }),
    );
    return parts.length ? parts.join(" · ") : null;
  };
  const DRUG_SCREEN_LABELS: Record<string, string> = {
    WILL_NOT_PASS: "Won't pass drug screen",
    WILL_PASS: "Will pass drug screen",
    WILL_PASS_EX_THC: "Will pass (except THC)",
  };
  const formatDrugAndBackground = () => {
    const drugScreenStatus = profile?.profile.drugScreenStatus;
    const hasCriminalRecord = profile?.profile.hasCriminalRecord;
    const parts = [
      drugScreenStatus ? DRUG_SCREEN_LABELS[drugScreenStatus] : null,
      hasCriminalRecord == null
        ? null
        : hasCriminalRecord
          ? "Criminal record disclosed"
          : "No criminal record",
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  };

  // Declared above `rows` because the row list reads them. All no-op without a
  // signed-in staffer (an edit needs an author to attribute the trail row to)
  // and on read-only sheets (the embedded review variant).
  const renderShiftEditor = () => {
    if (isReadOnly || !currentUser) return null;
    return (
      <ShiftPreferenceEditor
        onDone={() => {
          setEditingLabel(undefined);
        }}
        shiftPreferences={profile?.shiftPreferences ?? []}
        stafferId={currentUser.id}
        userId={workerId}
      />
    );
  };

  const renderPayEditor = () => {
    if (isReadOnly || !currentUser) return null;
    return (
      <PayEditor
        currentPay={currentPay ?? null}
        desiredPay={desiredPay ?? null}
        onDone={() => {
          setEditingLabel(undefined);
        }}
        stafferId={currentUser.id}
        userId={workerId}
      />
    );
  };

  const renderLocationEditor = () => {
    if (isReadOnly || !currentUser) return null;
    return (
      <LocationEditor
        candidacyId={candidacyId ?? null}
        onDone={() => {
          setEditingLabel(undefined);
        }}
        stafferId={currentUser.id}
        userId={workerId}
      />
    );
  };

  const renderDrugAndBackgroundEditor = () => {
    if (isReadOnly || !currentUser) return null;
    return (
      <DrugAndBackgroundEditor
        drugScreenStatus={profile?.profile.drugScreenStatus ?? null}
        hasCriminalRecord={profile?.profile.hasCriminalRecord ?? null}
        onDone={() => {
          setEditingLabel(undefined);
        }}
        stafferId={currentUser.id}
        userId={workerId}
      />
    );
  };

  const rows: {
    detail?: string;
    // Replaces the row's value while this row is the one being edited. Its
    // presence is also what makes the row's icon an edit affordance.
    editor?: ReactNode;
    Icon: LucideIcon;
    label: string;
    needsFollowUp?: boolean;
    sources?: PaperTrailCitationSource[];
    value: string | null;
  }[] = [
    {
      editor: renderPayEditor(),
      Icon: DollarSignIcon,
      label: "Pay",
      needsFollowUp: payNeedsFollowUp,
      sources: toCitationSources(payCitationRows, {
        currentCandidacyId: candidacyId ?? undefined,
        stafferNamesById,
      }),
      value: payParts.length ? payParts.join(" · ") : null,
    },
    {
      detail:
        profile?.isOpenToRelocation != null
          ? `Open to relocation: ${profile.isOpenToRelocation ? "Yes" : "No"}`
          : undefined,
      editor: renderLocationEditor(),
      Icon: MapPinIcon,
      label: "Commute & location",
      needsFollowUp: commuteNeedsFollowUp,
      // The row's value is computed commute, so the call's take rides on the
      // citation as a badge rather than replacing it.
      sources: toAiCallInsightCitationSource(
        aiCallInsights?.locationAndCommute?.citation,
        commuteCallNotes ? { changes: [{ value: commuteCallNotes }] } : {},
      ),
      value: commuteParts.length ? commuteParts.join(" · ") : null,
    },
    {
      editor: renderShiftEditor(),
      Icon: ClockIcon,
      label: "Shift",
      needsFollowUp: shiftNeedsFollowUp,
      sources: toCitationSources(shiftCitationRows, {
        currentCandidacyId: candidacyId ?? undefined,
        stafferNamesById,
      }),
      value: formatShiftStances(),
    },
    {
      detail: candidacyInfo?.motivationDetail ?? undefined,
      Icon: BriefcaseIcon,
      label: "Motivation",
      needsFollowUp: candidacyInfo?.motivationNeedsFollowUp,
      sources: toAiCallInsightCitationSource(
        aiCallInsights?.motivation?.citation,
      ),
      // Falls back to the deprecated top-level field so the row survives a
      // guac that predates aiCallInsights.
      value:
        aiCallInsights?.motivation?.value ?? candidacyInfo?.motivation ?? null,
    },
    {
      editor: renderDrugAndBackgroundEditor(),
      Icon: ShieldCheckIcon,
      label: "Drug and background",
      needsFollowUp: drugAndBackgroundNeedsFollowUp,
      sources: toCitationSources(drugAndBackgroundCitationRows, {
        currentCandidacyId: candidacyId ?? undefined,
        stafferNamesById,
      }),
      value: formatDrugAndBackground(),
    },
  ];

  /* Render */
  const renderFollowUpChip = () => {
    return (
      <Text className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-normal text-amber-700">
        Needs follow-up
      </Text>
    );
  };

  // An editable row's icon is its edit affordance: the field icon at rest,
  // swapping to a pencil on hover. Rows without an editor keep a plain glyph.
  const renderLabelIcon = (row: (typeof rows)[number]) => {
    if (!row.editor) {
      return (
        <row.Icon
          className={cn("h-4 w-4 shrink-0 text-slate-400")}
          strokeWidth={1.5}
        />
      );
    }

    return (
      <Button
        className="group/edit-icon h-5 w-4 shrink-0 p-0 hover:bg-transparent"
        onClick={() => {
          setEditingLabel(row.label);
        }}
        title={`Edit ${row.label.toLowerCase()}`}
        type="button"
        variant="ghost"
      >
        <row.Icon
          className="h-4 w-4 shrink-0 text-slate-400 group-hover/edit-icon:hidden"
          strokeWidth={1.5}
        />
        <PencilIcon
          className="hidden h-4 w-4 shrink-0 text-slate-700 group-hover/edit-icon:block"
          strokeWidth={1.5}
        />
      </Button>
    );
  };

  const renderLabel = (row: (typeof rows)[number]) => {
    return (
      <Row className="h-5 items-center gap-3 whitespace-nowrap">
        {renderLabelIcon(row)}
        <Text className="translate-y-px whitespace-nowrap font-plex-mono text-[11px] font-medium uppercase leading-5 tracking-wider text-slate-500">
          {row.label}
        </Text>
      </Row>
    );
  };

  const renderValue = (
    value: string | null,
    needsFollowUp: boolean | undefined,
  ) => {
    return (
      <Row className="min-w-0 flex-1 flex-wrap items-center gap-2">
        <Text
          className={cn(
            "text-sm font-normal",
            value ? "text-[#121212] dark:text-[#FAFAFA]" : "text-slate-400",
          )}
        >
          {value ?? EMPTY_STATE_MESSAGE}
        </Text>
        {needsFollowUp ? renderFollowUpChip() : null}
      </Row>
    );
  };

  const renderDetail = (detail: string | undefined) => {
    if (!detail) return null;
    return <Text className="text-xs font-normal text-slate-500">{detail}</Text>;
  };

  const renderEvaluationControl = (
    label: string,
    value: string | null,
    detail: string | undefined,
  ) => {
    if (!candidacyId) return null;
    return (
      <AddLogisticsToEvaluationControl
        candidacyId={candidacyId}
        subtitle={[value ?? EMPTY_STATE_MESSAGE, detail]
          .filter(Boolean)
          .join(" — ")}
        title={label}
      />
    );
  };

  // The open editor takes the value's place, so a row never shows both a value
  // and the controls editing it. Double-clicking the value is the second way in,
  // alongside the icon.
  const renderRowValue = (row: (typeof rows)[number]) => {
    if (row.editor && editingLabel === row.label) {
      return (
        <Column className="min-w-0 flex-1" ref={editorRef}>
          {row.editor}
        </Column>
      );
    }
    if (!row.editor) return renderValue(row.value, row.needsFollowUp);

    return (
      <Row
        className="min-w-0 flex-1 cursor-text"
        onDoubleClick={() => {
          setEditingLabel(row.label);
        }}
      >
        {renderValue(row.value, row.needsFollowUp)}
      </Row>
    );
  };

  const render = () => {
    return (
      <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-3 pl-2">
        {rows.map((row) =>
          row.sources?.length ? (
            <CandidacySourceCitations
              action={renderEvaluationControl(row.label, row.value, row.detail)}
              key={row.label}
              labelContent={renderLabel(row)}
              onOpenSheetTab={onOpenSheetTab}
              sources={row.sources}
            >
              {renderRowValue(row)}
              {renderDetail(row.detail)}
            </CandidacySourceCitations>
          ) : (
            <div
              className="col-span-2 grid grid-cols-subgrid items-start gap-x-3 py-1"
              key={row.label}
            >
              {renderLabel(row)}
              <Column className="group/evaluation min-w-0 gap-0.5 pl-3">
                <Row className="min-h-5 min-w-0 items-start justify-between gap-2">
                  {renderRowValue(row)}
                  {renderEvaluationControl(row.label, row.value, row.detail)}
                </Row>
                {renderDetail(row.detail)}
              </Column>
            </div>
          ),
        )}
      </div>
    );
  };

  return render();
};
